import { useEffect, useRef, useState } from 'react';
import { loadAdSense, isCrawlerUserAgent } from './loadAdSense';

interface GoogleAdProps {
  client: string; // ca-pub-XXXX
  slot: string;   // data-ad-slot id from AdSense
  layout?: 'in-article' | 'display';
  format?: 'auto' | 'fluid' | 'rectangle' | 'autorelaxed';
  fullWidthResponsive?: boolean;
  className?: string;
  style?: React.CSSProperties;
  layoutKey?: string; // data-ad-layout-key for in-feed fluid units
}

export default function GoogleAd({
  client,
  slot,
  layout = 'display',
  format = 'auto',
  fullWidthResponsive = true,
  className,
  style,
  layoutKey
}: GoogleAdProps) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [isInView, setIsInView] = useState(false);

  useEffect(() => {
    if (typeof window === 'undefined') return; // SSR: skip

    const ua = navigator.userAgent || '';
    if (isCrawlerUserAgent(ua)) {
      return; // Skip for crawlers
    }

    if (!containerRef.current) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsInView(true);
            observer.disconnect();
          }
        });
      },
      { root: null, rootMargin: '300px', threshold: 0 }
    );

    observer.observe(containerRef.current);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!isInView) return;

    let cancelled = false;
    loadAdSense(client)
      .then(() => {
        if (cancelled) return;
        try {
          (window.adsbygoogle = window.adsbygoogle || []).push({});
        } catch (e) {
          // no-op
        }
      })
      .catch(() => {
        // no-op
      });

    return () => {
      cancelled = true;
    };
  }, [isInView, client]);

  // Reserve space to avoid layout shift
  const baseStyle: React.CSSProperties = {
    minHeight: '280px',
    display: 'block',
    width: '100%'
  };

  const insProps: Record<string, any> = {
    className: 'adsbygoogle',
    style: { ...baseStyle, ...style },
    'data-ad-client': client,
    'data-ad-slot': slot,
    'data-full-width-responsive': fullWidthResponsive ? 'true' : 'false',
  };

  if (format === 'autorelaxed') {
    insProps['data-ad-format'] = 'autorelaxed';
    // Multiplex/Auto relaxed does not require in-article layout
  } else if (layout === 'in-article') {
    insProps['data-ad-format'] = 'fluid';
    insProps['data-ad-layout'] = 'in-article';
  } else {
    insProps['data-ad-format'] = format;
  }

  if (layoutKey) {
    insProps['data-ad-layout-key'] = layoutKey;
  }

  return (
    <div ref={containerRef} className={className}>
      <ins {...insProps} />
    </div>
  );
}


